import { useCallback, useEffect } from 'react';
import Breadcrumbs from '@v2/shared/components/Breadcrumbs';
import Spinner from '@v2/shared/components/Spinner';
import useIntersectionObserver from '@v2/shared/lib/useIntersectionObserver';
import useUrlGenerator from '@v2/shared/lib/useUrlGenerator';
import CategorySidebar from './CategorySidebar';
import GridHeader from './GridHeader';
import MagazineGrid from './MagazineGrid';
import PlanCta from './PlanCta';
import useMagazines from './useMagazines';
import { Props } from './types';

/**
 * "View Available Magazines" — what a Zinio Unlimited subscription contains, previously
 * served by headless_jshop at the same url.
 *
 * Everything on it is Zinio's: the categories arrive in the props, and the covers come
 * from /services_api/storefront/v1/third_party/ZinioUnlimited/products as the visitor
 * picks and scrolls. Nothing here is one of our products, and nothing here is for sale —
 * the one thing being sold is the subscription, and PlanCta is the way to it.
 *
 * The sibling of MagzterGoldMagazines, deliberately built the same way. Where it differs
 * — no icons, no prices, no `?offer=` carried across to the landing page — the difference
 * is Zinio's rather than this page's, and each one is noted where it lands.
 */
export default function ZinioUnlimitedMagazines({ categories }: Props) {
  const { buildUrl } = useUrlGenerator();
  const {
    magazines,
    isLoading,
    hasFailed,
    hasMore,
    loadMore,
    searchInput,
    setSearchInput,
    selectedCategoryId,
    selectCategory,
  } = useMagazines();

  const [sentinelRef, isSentinelVisible] = useIntersectionObserver({ rootMargin: '400px' });

  /**
   * Picking a category puts you back at the top of the page.
   *
   * Not a flourish: the new list starts empty, so without this a visitor who was deep in
   * the last category is left parked at the bottom of a page with nothing on it, and the
   * sentinel keeps asking for another page until the grid finally grows back past them.
   *
   * The page rather than the grid column. The column begins below the breadcrumbs and the
   * top of the viewport is behind the fixed header, so aligning to it would tuck the
   * search bar up under the header — and scroll DOWN to do it when the visitor was
   * already at the top. That was the review point on the Magzter Gold page; this one is
   * built with the answer already in it.
   */
  const chooseCategory = useCallback(
    (categoryId: string | null) => {
      selectCategory(categoryId);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [selectCategory]
  );

  // No query string on this link, unlike Magzter Gold's. There is no `?offer=` on this
  // journey to carry: the landing page advertises a recurring price it never discounts,
  // and headless's middleware excluded this url from offer persistence for the same
  // reason. See ZinioUnlimitedMagazinesPagePropsRequest.
  const landingUrl = buildUrl('/zinio-unlimited');

  useEffect(() => {
    if (isSentinelVisible && hasMore && !isLoading) {
      loadMore();
    }
  }, [isSentinelVisible, hasMore, isLoading, loadMore]);

  const selected =
    categories.find((category) => category.id === selectedCategoryId) ?? categories[0];

  // A site whose catalogue could not be reached has no sidebar and no heading to draw, and
  // a page consisting of one button is worse than saying so.
  if (!selected) {
    return (
      <div className="py-16 text-center text-lg text-slate-600">
        These magazines are not available right now. Please try again shortly.
      </div>
    );
  }

  // The box, its measure and its spacing are all Layouts.Full's.
  return (
    <>
      <Breadcrumbs
        items={[
          { label: 'Home', href: '/' },
          { label: 'ZINIO Unlimited', href: '/zinio-unlimited' },
          { label: 'Available Magazines' },
        ]}
      />

      <div className="grid grid-cols-12 gap-6">
        <CategorySidebar
          categories={categories}
          selectedId={selectedCategoryId}
          onSelect={chooseCategory}
        />

        <div className="col-span-12 md:col-span-7 lg:col-span-9">
          <GridHeader
            categories={categories}
            selected={selected}
            onSelect={chooseCategory}
            search={searchInput}
            onSearchChange={setSearchInput}
            isSearching={isLoading}
          />

          {magazines.length > 0 && <MagazineGrid magazines={magazines} />}

          {isLoading && (
            <div className="py-8 text-center text-3xl text-slate-400">
              <Spinner />
            </div>
          )}

          {!isLoading && hasFailed && (
            <p className="py-12 text-center text-lg text-slate-600">
              We could not load these magazines. Please try again shortly.
            </p>
          )}

          {!isLoading && !hasFailed && magazines.length === 0 && (
            <p className="py-12 text-center text-lg text-slate-600">No magazines found</p>
          )}

          {/*
            The trigger for the next page. A zero-height element below the grid rather
            than a ref on the last card, which is where headless_jshop put it: the observer
            binds once here instead of being re-attached to a different card after every
            page.
          */}
          <div ref={sentinelRef} aria-hidden="true" />
        </div>
      </div>

      <PlanCta href={landingUrl} />
    </>
  );
}

ZinioUnlimitedMagazines.layoutName = 'Layouts.Full';
